import { Helpers } from "./helpers/helpers";
import _ = require("lodash");
import { Category, FilterParameter, SearchResults } from "./models/types";


export abstract class FilterableList<TResult> {
	filterWrapper: HTMLElement;
	selectedFilterList: HTMLElement;
	filterChangeDebounced: _.DebouncedFunc<() => void>;
	resultsWrapper: HTMLElement;
	paginationContainer: HTMLElement;
	noResultsContainer: HTMLElement;
	currentFilters: FilterParameter[];
	public searchText: string;
	public bookmarksOnly = false;
	public sortOrder: string;
	private currentPageIndex = 1;
	locationFilterSelect: HTMLSelectElement;
	isFilterTabs = false;
	public pageSize: number;
	private mobileFilterBar: HTMLElement;
	constructor(resultListSelector: string, noResultsSelector = ".no-results", filterWrapperSelector = ".filters-container", pageSize: number = 12) {
		this.filterWrapper = document.querySelector(filterWrapperSelector) as HTMLElement;
		this.selectedFilterList = document.querySelector('.selected-filter-list') as HTMLElement;
		this.noResultsContainer = document.querySelector(noResultsSelector) as HTMLElement;
		this.resultsWrapper = document.querySelector(resultListSelector) as HTMLElement;
		this.paginationContainer = document.querySelector('.pagination-container') as HTMLElement;
		this.locationFilterSelect = document.querySelector('.location-filter select') as HTMLSelectElement;
		this.mobileFilterBar = document.querySelector('.mobile-filter-bar') as HTMLElement;
		
		this.pageSize = pageSize;
		if (Helpers.elementExists(resultListSelector)) {
			if (Helpers.isNullOrUndefined(this.filterWrapper)) {
				this.toggleLoader(false);
			} else {				
				this.initPagination();
				if (filterWrapperSelector !== '.filter-tabs') {
					this.initFilterChangedCallback();
					this.setFilterCheckboxes();
					this.initSelectedFilterRemove();
				} else {
					this.initFilterTabs();
				}
				this.initViewResultsButton();
			}
		}
	}
	/** Resets pagination back to page 1, e.g. when the sort order changes. */
	resetToFirstPage(): void {
		this.currentPageIndex = 1;
	}

	initViewResultsButton() {
		document.querySelector('.results-button')?.addEventListener('click', (e) => {
			e.preventDefault();
			Helpers.scrollToElement(this.selectedFilterList, 100, -125);
			this.filterWrapper.querySelector('.filters').classList.remove('open');
		});
	}

	abstract createResult(dataItem: TResult): void;
	abstract clearResults(): void;
	abstract toggleLoader(isLoading: boolean): void;
	
	/**
	 * Called when filters or pagination changes
	 * @param selectedFilters
	 */
	abstract onQueryChange(selectedFilters: FilterParameter[]): void;

	bindFilters(filterList: HTMLElement, changeEvent: (event: Event) => void) {
		filterList.querySelectorAll('input').forEach(input => {
			input.addEventListener("change", changeEvent);
		});
	}


	search(searchUrl: string, filters: FilterParameter[], appendResults: boolean, onComplete: (results:SearchResults<TResult>) => void = null, pageSize = 12, className=""): void {
		const self = this;
		const pageIndex = this.currentPageIndex;
		const request = new XMLHttpRequest();
		this.toggleLoader(true);
		request.open("POST", searchUrl, true);
		request.setRequestHeader("Content-Type", "application/json");

		request.onload = function () {
			let searchResults: SearchResults<TResult>;
			if (this.status >= 200 && this.status < 400) {
				searchResults = JSON.parse(this.response) as SearchResults<TResult>;
				const loginMessage = self.noResultsContainer.querySelector('.login-message');
				const genericMessage = self.noResultsContainer.querySelector('.generic-message');
				if (searchResults.results.length === 0 && pageIndex === 1) {
					const loggedInTypes = [
						'Declaration of Performance',
						'Installation Guide',
						'Marketing Case Study',
						'Success Stories',
						'Information Bulletins',
						'Model Specifications'];
					self.noResultsContainer.classList.remove('d-none');
					let foundContentType = false;
					if (!Helpers.isNullOrUndefined(self.selectedFilterList)) {
						self.selectedFilterList.querySelectorAll('li').forEach(li => {

							if (loggedInTypes.indexOf(li.innerText) > -1 ||
								loggedInTypes.filter(t => li.innerText.indexOf(t) > -1).length > 0) {
								foundContentType = true;
							}
						});
					}

					if (foundContentType) {
						loginMessage?.classList.remove('d-none');
						genericMessage?.classList.add('d-none');
					} else {
						loginMessage?.classList.add('d-none');
						genericMessage?.classList.remove('d-none');
					}
					self.updateTotalResults(searchResults);
				} else {
					self.noResultsContainer.classList.add('d-none');
					loginMessage?.classList.add('d-none');
					self.loadResults(searchResults);
				}
			} else {
				console.error("search() failed", this);

			}

			if (onComplete !== null) {
				onComplete(searchResults);
			}

			self.toggleLoader(false);
		}

		request.onerror = function () {
			console.error("search() error", this);
			self.toggleLoader(false);
		}
		var urlParameters = Helpers.getUrlParameters();
		const expandedFilters = this.expandChildIds(filters);
		const searchRequest = {
			pageIndex: pageIndex,
			filters: expandedFilters,
			culture: Helpers.getCurrentCulture(),
			pageSize: pageSize,
			className: className,
			technical: urlParameters['technical'] ? urlParameters['technical'] === "true" : false,
			searchText: this.searchText,
			bookmarksOnly: this.bookmarksOnly,
			sortOrder: this.sortOrder
		};

		if (!appendResults) {
			this.clearResults();
		}

		request.send(JSON.stringify(searchRequest));
	}

	updateUrlWithFilters(parameters: Array<FilterParameter>) {
		let filterUrl: any = {};
		const queryString: string[] = [];
		if (parameters.length !== 0) {
			parameters.forEach(parameter => {
				let formattedFilterParameter = "";
				if (parameter.categoryIds.length) {
					formattedFilterParameter += parameter.categoryParent + ",";
					// Use code names in URL when available for environment-agnostic links
					const hasCodeNames = parameter.categoryIds.every(c => c.codeName);
					formattedFilterParameter += parameter.categoryIds.map(c => hasCodeNames ? c.codeName : c.id).join(",");
					queryString.push(formattedFilterParameter);
				} else if (parameter.stateCode) {
					filterUrl["stateCode"] = parameter.stateCode;
				}
				else if (parameter.countryCode) {
					filterUrl["countryCode"] = parameter.countryCode;
				}
			});
			if (queryString.length) {
				filterUrl["filters"] = queryString.join("~");
			}
		}

		filterUrl["page"] = this.currentPageIndex;
		const location = this.locationFilterSelect?.value;
		if (location) {
			filterUrl["location"] = location;
		}

		//check other querystrings 
		const knownValues = ["page", "location","filters"];
		const params = Helpers.getUrlParameters();
		knownValues.forEach(v=> delete params[v]); 
		filterUrl = {...filterUrl, ...params};
		Helpers.updateUrl(filterUrl);
	}

	getSelectedFilters(filterLists: NodeListOf<HTMLElement> = null): Array<FilterParameter> {


		if (this.isFilterTabs) {
			return this.getSelectedFilterTab();
		}

		if (filterLists === null) {
			filterLists = document.querySelectorAll('.filter-list');
		}



		const selectedFilters = new Array<FilterParameter>();
		filterLists.forEach(list => {
			const categoryParent = list.dataset.category;
			const categoryIds: Category[] = [];
			const filterParameter = {} as FilterParameter;
			const selectedCheckboxes = list.querySelectorAll("input[type=checkbox]:checked");

			selectedCheckboxes.forEach(checkbox => {
				const checkboxElement = checkbox as HTMLInputElement;
				const parentLabel = checkboxElement.closest('label');
				const category = {
					id: parseInt(checkboxElement.value),
					displayName: parentLabel.innerText,
					codeName: checkboxElement.dataset.codename || ""
				}
				categoryIds.push(category);
			});

			if (categoryIds.length > 0) {
				filterParameter.categoryParent = categoryParent;
				filterParameter.categoryIds = categoryIds;
				selectedFilters.push(filterParameter);
			}
		});

		const location = this.locationFilterSelect?.value;
		if (location) {
			selectedFilters.push({
				categoryIds: [],
				categoryParent: "location",
				codeName: location,
				stateCode:"",
				countryCode:""
			});
		}

		return selectedFilters;
	}

	/**
	 * For parent-only filter mode: expands filters by appending child IDs
	 * stored in data-child-ids attributes on checked parent checkboxes.
	 * Returns a new array without mutating the original.
	 */
	private expandChildIds(filters: FilterParameter[]): FilterParameter[] {
		if (!this.filterWrapper) return filters;

		return filters.map(filter => {
			const extraIds: Category[] = [];
			const list = this.filterWrapper.querySelector(`.filter-list[data-category="${filter.categoryParent}"]`);
			if (!list) return filter;

			filter.categoryIds.forEach(cat => {
				const checkbox = list.querySelector(`input[type=checkbox][value="${cat.id}"]`) as HTMLInputElement;
				const childIdsAttr = checkbox?.dataset.childIds;
				if (childIdsAttr) {
					childIdsAttr.split(',').forEach(idStr => {
						const id = parseInt(idStr);
						if (!isNaN(id) && !filter.categoryIds.some(c => c.id === id) && !extraIds.some(c => c.id === id)) {
							extraIds.push({ id, displayName: cat.displayName });
						}
					});
				}
			});

			if (extraIds.length === 0) return filter;

			return {
				...filter,
				categoryIds: [...filter.categoryIds, ...extraIds]
			};
		});
	}

	pageChanged() {
		const selectedFilters = this.getSelectedFilters();
		this.updateUrlWithFilters(selectedFilters);
		this.onQueryChange(selectedFilters);
	}
	getSelectedFilterTab(): FilterParameter[] {
		let selectedFilters: FilterParameter[] = [];
		const activeTab = this.filterWrapper.querySelector('a.active') as HTMLElement;
		const category = activeTab.dataset.categoryid;
		if (category) {

			selectedFilters = [
				{
					categoryParent: "",
					categoryIds: [{ id: parseInt(category), displayName: "" }],
					codeName: "",
					stateCode:"",
					countryCode:""
				}
			];
		}

		return selectedFilters;
	}
	initFilterTabs() {
		this.isFilterTabs = true;
		const parameters = Helpers.getUrlParameters();

		const refreshUi = (category: string, resetPage = false) => {
			let selectedFilters: FilterParameter[] = [];
			if (category) {

				selectedFilters = [
					{
						categoryParent: "",
						categoryIds: [{ id: parseInt(category), displayName: "" }],
						codeName: "",
						stateCode:"",
						countryCode:""
					}
				];
			}

			if (resetPage) {
				this.currentPageIndex = 1;
			}
			this.updateUrlWithFilters(selectedFilters);
			this.onQueryChange(selectedFilters);
		}

		if (parameters.filters) {
			const categoryId = parameters.filters;

			this.filterWrapper.querySelectorAll('a').forEach(link => link.classList.remove('active'));
			const activeLink = this.filterWrapper.querySelector('[data-categoryid="' + categoryId.replace('-', '') + '"]') as HTMLElement;
			activeLink?.classList.add('active');
			refreshUi(categoryId.replace('-', ''));

		}
		this.filterWrapper.querySelectorAll('a').forEach(filter => {
			filter.addEventListener('click', (e) => {
				e.preventDefault();
				const linkElement = (e.target as HTMLElement);
				const category = linkElement.dataset.categoryid;
				this.filterWrapper.querySelectorAll('a').forEach(link => link.classList.remove('active'));
				linkElement.classList.add('active');

				refreshUi(category, true);
			});
		});
	}

	initPagination() {
		const parameters = Helpers.getUrlParameters();
		if (parameters['page']) {
			this.currentPageIndex = parseInt(parameters['page']);
		}
		this.paginationContainer?.addEventListener('click', (e) => {
			let clickedElement = (e.target as HTMLElement);

			if (clickedElement.tagName === 'A' || clickedElement.tagName === 'svg') {
				if (clickedElement.tagName === 'svg') {
					clickedElement = clickedElement.closest('a');
				}

				e.preventDefault();
				const pageNumber = clickedElement.dataset.pagenumber;
				this.currentPageIndex = parseInt(pageNumber);

				this.paginationContainer.querySelectorAll('li').forEach(li => { li.classList.remove('active'); });
				clickedElement.closest('li').classList.add('active');
				Helpers.scrollToElement(this.resultsWrapper, 10, -200);

				this.pageChanged();
			}
		});
	}
	c = false;
	initFilterChangedCallback(): void {
		const self = this;		
		if (!window.mobileSidebarToggleRegistered) {
			console.log(this.c, 'c');
			window.mobileSidebarToggleRegistered = true;
			
			const mobileFilterToggle = this.mobileFilterBar?.querySelector('.filter-toggle a') as HTMLElement;
			mobileFilterToggle?.addEventListener('click', (e) => {
				e.preventDefault();
				this.filterWrapper.classList.toggle('open');

			})
			
		}
		this.filterWrapper.querySelector('.filter-header .close')?.addEventListener('click', (e)=> {
			this.filterWrapper.classList.remove('open');
		});

		this.filterWrapper.querySelector('.filter-footer .apply')?.addEventListener('click', (e)=> {
			e.preventDefault();
			this.filterWrapper.classList.remove('open');
		});

		this.filterWrapper.querySelector('.filter-footer .clear')?.addEventListener('click', (e)=> {
			e.preventDefault();
			this.clearFilters();
			this.filterWrapper.classList.remove('open');
		});

		// Attached directly to each trigger (rather than delegated) so it fires before the click
		// bubbles up to the filter-list's <h2>, which toggles the accordion open/closed on click
		// (see filters.ts) - stopPropagation here prevents that unwanted side effect.
		this.filterWrapper.querySelectorAll('.select-all-trigger').forEach(trigger => {
			trigger.addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();

				const filterList = (trigger as HTMLElement).closest('.filter-list');
				if (!filterList) return;

				filterList.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
					(checkbox as HTMLInputElement).checked = true;
				});
				filterList.querySelectorAll('li.has-children').forEach(li => li.classList.add('open'));

				self.filterChangeDebounced();
			});
		});
		
		this.filterChangeDebounced = _.debounce(() => {
			
			const selectedFilters = this.getSelectedFilters();
			if (self.currentFilters && !Helpers.equalObjects(self.currentFilters, selectedFilters)) {
				this.currentPageIndex = 1;
			}

			this.currentFilters = selectedFilters;

			self.updateUrlWithFilters(selectedFilters);
			self.onQueryChange(selectedFilters);
			self.updateSelectFilterList();
		}, 800);

		this.filterWrapper.addEventListener("click", (event) => {
			const target = event.target as HTMLInputElement;
			if (target.type === "checkbox") {
				self.filterChangeDebounced();
				const parentLi = (target as HTMLElement).closest('li');

				if (parentLi.classList.contains("has-children")) {
					const parentChecked = (parentLi.querySelector('.parent input[type="checkbox"]') as HTMLInputElement).checked;
					if (parentChecked) {
						parentLi.classList.add('open');
					} else {
						parentLi.classList.remove('open');
					}
					parentLi.querySelectorAll('ul input[type="checkbox"]').forEach(cb => {
						(cb as HTMLInputElement).checked = parentChecked;
					});
				}
			}
		});

		this.locationFilterSelect?.addEventListener("change", () => {
			self.filterChangeDebounced();
		});

		window.addEventListener('popstate', function () {
			const parameters = Helpers.getUrlParameters();
			if (!Helpers.isNullOrUndefined(parameters["page"])) {
				self.currentPageIndex = parseInt(parameters["page"]);
			}

			const checkboxes = self.filterWrapper.querySelectorAll('input[type=checkbox]');
			checkboxes.forEach(checkbox => {
				(checkbox as HTMLInputElement).checked = false;
			});;

			self.setFilterCheckboxes();

		});
	}

	setFilterCheckboxes() {
		const parameters = Helpers.getUrlParameters();
		if (!Object.prototype.hasOwnProperty.call(parameters, "filters") 
			&& !Object.prototype.hasOwnProperty.call(parameters, "location")
			&& !Object.prototype.hasOwnProperty.call(parameters, "stateCode")
			&& !Object.prototype.hasOwnProperty.call(parameters, "countryCode")
		) {
			this.resultsWrapper.classList.remove('d-none');
			this.toggleLoader(false);
			return;
		}
		document.querySelector('.section-expandable.expanded')?.classList.remove('expanded');
		//set checkboxes to checked based on URL parameters
		if (Object.prototype.hasOwnProperty.call(parameters,"filters")) {
			const filtersValue: string = parameters["filters"];
			// Detect new delimiter format (~ between groups, , between values)
			// vs legacy format (_ between groups, - between values)
			const isNewFormat = filtersValue.indexOf("~") !== -1 || filtersValue.indexOf(",") !== -1;
			const groupSep = isNewFormat ? "~" : "_";
			const valueSep = isNewFormat ? "," : "-";

			const filterGroups: string[] = filtersValue.split(groupSep);
			filterGroups.forEach(group => {
				const parts = group.split(valueSep);
				const parentId = parts[0];
				const categoryList = this.filterWrapper.querySelector('.filter-list[data-category="' + parentId + '"]');
				
				parts.forEach(id => {
					if (id === parentId) return;
					if (!isNaN(parseInt(id))) {
						// Legacy numeric ID support
						const checkbox = categoryList?.querySelector('input[type=checkbox][value="' + id + '"]');
						if (checkbox) {
							(checkbox as HTMLInputElement).checked = true;
						}
					} else {
						// Code name support
						const checkbox = categoryList?.querySelector('input[type=checkbox][data-codename="' + id + '"]');
						if (checkbox) {
							(checkbox as HTMLInputElement).checked = true;
						}
					}
				});
			});
		}
		if (parameters["page"]) {
			this.currentPageIndex = parseInt(parameters["page"]);
		}

		if (parameters["location"] && this.locationFilterSelect) {
			this.locationFilterSelect.value = parameters["location"];
		}
		const countrySelect = document.querySelector('.country-filter') as HTMLSelectElement;
		if (parameters["countryCode"] && countrySelect) {
			countrySelect.value = parameters["countryCode"]
		}

		const stateSelect = document.querySelector('.state-filter') as HTMLSelectElement;
		if (parameters["stateCode"] && stateSelect) {
			stateSelect.value = parameters["stateCode"]
		}
		
		this.filterChangeDebounced();
	}

	updateSelectFilterList(): void {
		const selectedFilters = this.getSelectedFilters();
		const self = this;

		if (selectedFilters.length !== 0) {
			let ul = this.selectedFilterList.querySelector('ul');
			if (ul) {
				ul.remove();
			}
			ul = document.createElement("ul");


			selectedFilters.forEach(filter => {

				let displayName = "";
				let filterId = "";
				const categoryParent = filter.categoryParent;
				if (filter.categoryParent === "location") {
					displayName = `${filter.categoryParent}: ${filter.codeName}`;
					filterId = filter.codeName;
					self.createSelectedListItem(displayName, categoryParent, filterId, ul);
				} else {
					filter.categoryIds.forEach(c => {
						displayName = c.displayName;
						filterId = c.id.toString();
						self.createSelectedListItem(displayName, categoryParent, filterId, ul);
					});
				}



			});
			this.selectedFilterList.prepend(ul);
			this.selectedFilterList.classList.remove('d-none');

		} else {
			this.selectedFilterList.querySelector('ul')?.remove();
			this.selectedFilterList.classList.add('d-none');
		}
	}

	createSelectedListItem(displayName: string, categoryParent: string, filterId: string, ulToAppend: HTMLUListElement) {
		const closeIcon = document.querySelector('.close-icon .icon');
		const listItem = document.createElement("li");
		listItem.innerHTML = `${displayName} <a href="#" class="filter-remove" data-categoryParent=${categoryParent} data-filterId="${filterId}">${closeIcon.outerHTML}</a>`;
		ulToAppend.append(listItem);
	}

	initSelectedFilterRemove(): void {
		const self = this;
		this.selectedFilterList.addEventListener('click', (e) => {
			const clickedElem = e.target as HTMLElement;
			const closetRemove = clickedElem.closest('.filter-remove') as HTMLElement;
			if (closetRemove?.contains(clickedElem) || clickedElem.classList.contains('filter-remove')) {
				e.preventDefault();
				const categoryParent = closetRemove.dataset.categoryparent;
				if (categoryParent === "location" && this.locationFilterSelect) {
					this.locationFilterSelect.value = "";
				} else {
					const categoryId = parseInt(closetRemove.dataset.filterid);
					self.removeFilterById(categoryId);
				}
				closetRemove.closest('li').remove();
				self.filterChangeDebounced();
			}
		});

		this.selectedFilterList.querySelector('.clear-filters').addEventListener('click', (e) => {
			e.preventDefault();
			this.clearFilters();

		});
	}
	
	private clearFilters() {
		const checkboxes = this.filterWrapper.querySelectorAll('input[type=checkbox]:checked');
		
		checkboxes.forEach(checkbox => {
			(checkbox as HTMLInputElement).checked = false;
		});		
		if (this.locationFilterSelect) {
			this.locationFilterSelect.value = "";
		}
		this.filterChangeDebounced();
	}

	private updatePagination(currentPage: number, totalPages: number) {		
		if (this.paginationContainer) {
			const ul = this.paginationContainer.querySelector('ul');
			const nextLink = ul.querySelector('.next');
			const previousLink = ul.querySelector('.previous');
			const nextA = (nextLink.querySelector('a') as HTMLAnchorElement);
			const previousA = (previousLink.querySelector('a') as HTMLAnchorElement);


			ul.querySelectorAll('.page').forEach(l => { l.remove() });

			const liEllipses = document.createElement('li') as HTMLElement;
			liEllipses.classList.add("page");
			liEllipses.innerText = "...";
			let startPage = 1;
			let endPage = totalPages;
			if (totalPages > 10) {
				if (currentPage > 1) {
					startPage = currentPage - 1;
				}

				endPage = startPage + 9;
				if (endPage > totalPages) {
					startPage = totalPages - 9;
					endPage = totalPages;
				}

			}

			let nextPage = currentPage + 1;
			if (nextPage >= totalPages) {
				nextPage = totalPages;
			}
			let previousPage = currentPage - 1;
			if (previousPage < 0) {
				previousPage = 1;
			}

			if (previousPage >= totalPages) {
				previousPage = totalPages - 1;
			}


			nextA.href = '?page=' + (nextPage);
			nextA.dataset.pagenumber = (nextPage).toString();
			previousA.href = '?page=' + (previousPage);
			previousA.dataset.pagenumber = (previousPage).toString();

			let params = Helpers.getUrlParameters();

			if (params && params['filters']) {
				nextA.href += "&filters=" + params['filters'];
				previousA.href += "&filters=" + params['filters'];
			}
			
			if (totalPages > endPage && currentPage > 10) {
				const li = document.createElement('li') as HTMLElement;
				const a = document.createElement('a') as HTMLAnchorElement;
				a.dataset.pagenumber = "1";
				a.href = '?page=1';
				a.innerText = "1";

				if (params && params['filters']) {
					a.href += "&filters=" + params['filters'];					
				}
				
				li.classList.add("page");
				li.append(a);
				ul.insertBefore(li, nextLink);
				ul.insertBefore(liEllipses.cloneNode(true), nextLink);

			}

			for (let i = startPage; i <= endPage; i++) {
				const li = document.createElement('li') as HTMLElement;
				const a = document.createElement('a') as HTMLAnchorElement;
				a.dataset.pagenumber = i.toString();
				a.href = '?page=' + i;
				if (params && params['filters']) {
					a.href += "&filters=" + params['filters'];
				}
				a.innerText = i.toString();
				li.classList.add("page");
				li.append(a);
				if (currentPage === i) {
					li.classList.add('active'); }
				ul.insertBefore(li, nextLink);
			}


			if (totalPages > endPage) {
				const li = document.createElement('li') as HTMLElement;
				const a = document.createElement('a') as HTMLAnchorElement;
				a.dataset.pagenumber = totalPages.toString();
				a.href = '?page=' + totalPages;
				if (params && params['filters']) {
					a.href += "&filters=" + params['filters'];
				}
				a.innerText = totalPages.toString();
				li.classList.add("page");
				li.append(a);
				ul.insertBefore(liEllipses.cloneNode(true), nextLink);
				ul.insertBefore(li, nextLink);


			}

			if (totalPages > 1 && totalPages > currentPage) {
				nextLink?.classList.remove('d-none');
			} else {
				nextLink?.classList.add('d-none');
			}

			if (totalPages > 1 && currentPage > 1) {
				previousLink?.classList.remove('d-none');
			} else {
				previousLink?.classList.add('d-none');
			}
		}
	}
	private loadResults(searchResults: SearchResults<TResult>): void {
		const results = searchResults.results;
		this.clearResults();
		results.forEach(item => {
			this.createResult(item);
		});

		this.updateTotalResults(searchResults);

	}
	private updateTotalResults(searchResults: SearchResults<TResult>) {
		let totalResults = searchResults.results.length;

		this.updatePagination(searchResults.currentPage, searchResults.totalPages);

		if (searchResults.totalCount > 0) {
			totalResults = searchResults.totalCount;
		}
		
		this.updateTotalResultsElement(totalResults);
	}
	public updateTotalResultsElement(total: number) {
		const resultCount = this.filterWrapper.querySelector('.result-count');
		const filterCount = this.filterWrapper.querySelector('.filter-count');
		const mobileResultCount = document.querySelector('.mobile-filter-bar .result-count');
		if (resultCount) {
			resultCount.innerHTML = `${total} results`;
			resultCount.classList.remove('d-none');
		} 
		
		if (mobileResultCount) {
			mobileResultCount.innerHTML = `${total} results`;
			mobileResultCount.classList.remove('d-none');
		}
		
		if (filterCount) {
			const totalFilters =  this.filterWrapper.querySelectorAll('input[type=checkbox]:checked').length;
			filterCount.innerHTML = `(${totalFilters})`;
		}
	}
	private removeFilterById(id: number) {
		const valueSelector = `input[type="checkbox"][value="${id}"]`;
		const checkbox = this.filterWrapper.querySelector(valueSelector) as HTMLInputElement;
		checkbox.checked = false;
	}


}
